
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, LanguageContextType } from '@/types/language';
import { translations } from '@/i18n/translations';
import { getPreferredLanguage, setHtmlAttributes } from '@/utils/languageUtils';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    return getPreferredLanguage();
  });

  useEffect(() => {
    console.log('Setting language to:', currentLanguage);
    localStorage.setItem('preferredLanguage', currentLanguage);
    setHtmlAttributes(currentLanguage);
  }, [currentLanguage]);

  const t = (key: string): string => {
    // If key is not a string or is empty, return the key itself
    if (typeof key !== 'string' || key === '') {
      return key;
    }
    
    // Removed the length check to allow translation of longer text blocks
    
    if (!translations[currentLanguage]) {
      console.warn(`No translations found for language: ${currentLanguage}`);
      return key;
    }
    
    // Return the translation if it exists, otherwise fall back to the key
    const translation = translations[currentLanguage][key];
    if (translation) {
      return translation;
    }
    
    // For debugging purposes in development only
    // console.log(`No translation found for key: "${key}" in language: ${currentLanguage}`);
    
    // If the current language is not English and we don't have a translation,
    // check if there's an English translation available as a fallback
    if (currentLanguage !== 'en') {
      const enTranslation = translations['en'][key];
      if (enTranslation) {
        return enTranslation;
      }
    }
    
    return key;
  };

  const setLanguage = (lang: Language) => {
    console.log('Changing language to:', lang);
    setCurrentLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
